import { useLayoutEffect, useRef, useState } from 'react';

import { cn } from '../../lib/cn';

const TEXT_CLASS = 'break-words leading-6 text-content-muted';
const MORE = '… Lire plus';

/**
 * Longest prefix of `text` that, followed by the « Lire plus » tail, still fits in `lines`
 * lines at the element's width; `null` when the whole text already fits. Measured on a
 * hidden copy, because it depends on the column width and on line breaks in the text.
 */
function fitText(el: HTMLElement, text: string, lines: number): string | null {
  const probe = document.createElement('p');
  probe.className = TEXT_CLASS;
  probe.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;width:${el.clientWidth}px`;
  el.parentElement?.appendChild(probe);

  const limit = Number.parseFloat(getComputedStyle(probe).lineHeight) * lines + 1;
  probe.textContent = text;
  let result: string | null = null;
  if (probe.scrollHeight > limit) {
    let lo = 0;
    let hi = text.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      probe.textContent = text.slice(0, mid).trimEnd() + MORE;
      if (probe.scrollHeight <= limit) lo = mid;
      else hi = mid - 1;
    }
    result = text.slice(0, lo).trimEnd();
  }
  probe.remove();
  return result;
}

/**
 * Text cut to `lines` lines; « … Lire plus » follows the last visible word, inline, and calls
 * `onMore`. Line breaks become spaces: a list shows a flowing preview.
 */
export function ClampedText({
  text,
  lines = 3,
  onMore,
}: {
  text: string;
  lines?: 2 | 3;
  onMore: () => void;
}) {
  const flat = text.replace(/\s*\n+\s*/g, ' ');
  const [cut, setCut] = useState<string | null>(null);
  const ref = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setCut(fitText(el, flat, lines));
    // Column width changes (window resize, view switch) move the cut.
    const observer = new ResizeObserver(() => setCut(fitText(el, flat, lines)));
    observer.observe(el);
    return () => observer.disconnect();
  }, [flat, lines]);

  return (
    <p ref={ref} className={cn('w-full', TEXT_CLASS)}>
      {cut ?? flat}
      {cut === null ? null : (
        <>
          …{' '}
          <button
            type="button"
            onClick={onMore}
            className="font-medium text-brand-600 hover:underline"
          >
            Lire plus
          </button>
        </>
      )}
    </p>
  );
}
